import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { PitchKindCover } from '../live/PitchKindCover';
import { pitchKindName } from '../live/pitchKindCopy';
import type { CampaignAddRow } from '../outreachTypes';
import { addSourceText } from './resultsCopy';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

const DASH = '—';

/**
 * "Adds" (design outreach-v2 n1): each playlist or outlet that added the song, found by Plug in the
 * playlist data or marked from a reply, with followers, position and the streams from it (Spotify
 * for Artists playlist source; a dash with no number).
 */
const AddsTableComponent: React.FC<{ adds: CampaignAddRow[] }> = ({ adds }) => {
  const intl = useIntl();
  const followers = useMemo(() => adds.reduce((sum, add) => sum + (add.followers ?? 0), 0), [adds]);
  const rows = useMemo(
    () =>
      adds.map((add) => {
        const sub = [
          add.kind === null ? null : pitchKindName(intl, add.kind),
          intl.formatMessage(messages.addedOn, {
            date: intl.formatDate(new Date(add.foundAt), { month: 'short', day: 'numeric' }),
          }),
          addSourceText(intl, add.source),
        ].filter((part): part is string => part !== null);
        return (
          <div key={add.placementUuid} className={styles.tableRow} data-testid="adds-row">
            <PitchKindCover coverUrl={null} kind={add.kind} />
            <div className={styles.name}>
              <b>
                <a href={add.url} target="_blank" rel="noreferrer">
                  {add.name}
                </a>
              </b>
              <small>{sub.join(' · ')}</small>
            </div>
            <span className={styles.num}>{add.followers === null ? DASH : intl.formatNumber(add.followers)}</span>
            <span className={`${styles.num} ${styles.wide}`}>{add.position === null ? DASH : `#${add.position}`}</span>
            <span className={`${styles.num} ${styles.wide}`}>
              {add.streamsFromIt === null ? DASH : intl.formatNumber(add.streamsFromIt)}
            </span>
          </div>
        );
      }),
    [adds, intl]
  );
  return (
    <section className={`${card.card} ${card.cardFlush}`} data-testid="adds-table">
      <div className={card.cardHead}>
        <div>
          <h3 className={card.cardTitle}>{intl.formatMessage(messages.addsTitle)}</h3>
          <p className={card.cardIntro}>{intl.formatMessage(messages.addsIntro)}</p>
        </div>
        <span className={card.meta}>
          {intl.formatMessage(messages.addsMeta, {
            count: intl.formatNumber(adds.length),
            followers: intl.formatNumber(followers),
          })}
        </span>
      </div>
      {adds.length === 0 ? (
        <p className={`${shared.small} m-0 p-3`}>{intl.formatMessage(messages.addsNone)}</p>
      ) : (
        <>
          <div className={`${styles.tableRow} ${styles.tableHead}`} aria-hidden>
            <span />
            <span>{intl.formatMessage(messages.colWhere)}</span>
            <span className={styles.num}>{intl.formatMessage(messages.colFollowers)}</span>
            <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colPosition)}</span>
            <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colStreams)}</span>
          </div>
          {rows}
        </>
      )}
    </section>
  );
};

export const AddsTable = TypedMemo(AddsTableComponent);
